import React, { useState, useRef } from 'react';
import { Icons } from './Icons';

interface CollapseProps {
    title: string;
    children: React.ReactNode;
    height?: string | number;
    className?: string;
    footer?: React.ReactNode;
    allowFullscreen?: boolean;
}

export const Collapse: React.FC<CollapseProps> = ({
    title,
    children,
    height = '500px',
    className = '',
    footer = null,
    allowFullscreen = false
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const fullscreenRef = useRef<HTMLDivElement>(null);

    const toggleCollapse = () => {
        setIsOpen(!isOpen);
    };

    const enterFullscreen = async () => {
        if (!fullscreenRef.current) return;
        try {
            if (fullscreenRef.current.requestFullscreen) {
                await fullscreenRef.current.requestFullscreen();
            } else if ((fullscreenRef.current as any).webkitRequestFullscreen) {
                await (fullscreenRef.current as any).webkitRequestFullscreen();
            } else if ((fullscreenRef.current as any).msRequestFullscreen) {
                await (fullscreenRef.current as any).msRequestFullscreen();
            }
        } catch (err) {
            console.error('Error attempting to enable fullscreen:', err);
        }
    };

    const exitFullscreen = async () => {
        try {
            if (document.exitFullscreen) {
                await document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
                await (document as any).webkitExitFullscreen();
            } else if ((document as any).msExitFullscreen) {
                await (document as any).msExitFullscreen();
            }
        } catch (err) {
            console.error('Error attempting to exit fullscreen:', err);
        }
    };

    const toggleFullscreen = () => {
        if (isFullScreen) {
            exitFullscreen();
        } else {
            enterFullscreen();
        }
    };

    // Listen for fullscreen changes
    React.useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    return (
        <div className={`w-full ${isFullScreen ? 'h-full' : undefined} border-brand-primary  ${isOpen ? 'border-none' : 'border-b'} ${className}`}>
            <button
                className="w-full flex items-center justify-between py-4 px-2 border-none hover:bg-background-container-title transition-colors focus:outline-none focus:ring-0 focus:ring-offset-0"
                onClick={toggleCollapse}
                role="button"
                tabIndex={-1}
                type="button"
            >
                <span className="text-lg text-brand-primary">{title}</span>

                <div className="flex items-center space-x-2">

                    {isOpen && allowFullscreen && (
                        <div
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering collapse toggle
                                toggleFullscreen();
                            }}
                            className="p-1 rounded hover:bg-gray-200 transition-colors focus:outline-none"

                            title={isFullScreen ? "Exit fullscreen" : "Enter fullscreen"}
                        >
                            <div className="flex items-center">
                                {isOpen && <Icons.FullScreen />}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center">
                        {isOpen && <Icons.CaretDown />}
                        {!isOpen && <Icons.CaretUp />}
                    </div>
                </div>
            </button>
            <div ref={fullscreenRef} className={isFullScreen ? 'h-full' : undefined}>
                <div
                    ref={contentRef}
                    className="overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                        height: isOpen ? isFullScreen ? '100%' : height : '0px',
                        display: isOpen ? "block" : 'none'
                    }}
                >
                    <div className="py-4 px-2 h-full flex flex-col overflow-hidden">
                        {children}
                    </div>
                </div>
                <div className="overflow-hidden transition-all duration-300 ease-in-out" style={{
                    display: isOpen ? "block" : 'none'
                }}>
                    {footer}
                </div>
            </div>
        </div >
    );
};